/**
 * `AW48` · Las reglas de `R1-F2 §5.4` y `§5.6`, **puras y sin DOM**.
 *
 * Viven separadas del componente por el mismo motivo por el que los bordes viven
 * separados de las pantallas: así se pueden ejercer con un caso de prueba que no
 * monta nada y no toca la red. La regla que decide si un mensaje de contención se
 * guarda o no es la que más caro sale equivocada, y no puede estar enterrada en un
 * `onSubmit`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ DIEZ CAMPOS OBLIGATORIOS · NUEVE BAJO EL TOPE — Y LA DIFERENCIA IMPORTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⭐⭐ **RASTRO · `R2-UI9` · 23/9/2026.** Este título decía «NUEVE CAMPOS
 * OBLIGATORIOS · OCHO BAJO EL TOPE» y los dos números subieron uno por
 * `botonSinRespuesta` (`PRD_R2.md:1194–1203`). **El argumento no se movió**: lo
 * que separa los dos conteos sigue siendo `personalityProfile` y nada más.
 *
 * `§5.4`: *«Sin todos los textos cargados y al menos una categoría de guardrail,
 * el proceso no arranca.»* ⇒ **los diez son obligatorios**, y la alternativa
 * —*«un texto de respaldo escondido en el código»*— está descartada con nombre
 * porque reintroduce el modo de falla de que falte configuración, nadie se queje
 * y el producto elija solo.
 *
 * `§5.6` pone el tope de 4.096 **sólo en los textos que se le envían al
 * participante**: los siete del motor, los dos de derivación, y el mensaje y la
 * línea de ayuda de cada categoría. **Son NUEVE de los diez.**
 *
 * ⛔ **El décimo —el perfil de personalidad— NO lleva ese tope**, y meterlo
 * «porque es un texto más» rompe un caso positivo: *«nunca se envía como mensaje:
 * se le manda al modelo como prompt del sistema. Lleva un tope propio y mayor,
 * fundado en el presupuesto de tokens del turno»*. ⚠️ **Y ese valor propio está
 * PENDIENTE DEL TL** (`§12`), así que la pantalla **muestra el contador sin
 * bloquear** y este archivo no declara ningún número para él: uno inventado acá
 * sería la fuente que después nadie discute.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ EN CONTENCIÓN EL TOPE SE VERIFICA SOBRE LA **SUMA**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `§5.6`, textual: *«En las categorías de Contención el tope se verifica sobre
 * mensaje MÁS línea de ayuda concatenados, porque es lo que efectivamente sale
 * como un solo envío. **Verificar cada campo por separado deja pasar la suma, y
 * la suma es lo que se corta.**»*
 *
 * Es la regla de este archivo que se implementa mal si se lee rápido, y el
 * desenlace no es cosmético: el mensaje que se corta es el que le llega a un
 * menor en crisis, y lo que se pierde por el final es **la línea de ayuda**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ QUÉ NO HACE ESTE ARCHIVO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   · **No hace cumplir «al menos una categoría de Contención»** (`§5.4`). Esa
 *     regla la hace cumplir **el arranque del proceso**, no la pantalla: es un
 *     estado de la instancia entera y no de un formulario. La pantalla lo AVISA
 *     —para que el operador no se entere por un proceso que no levanta— y no lo
 *     bloquea: bloquear el borrado de la última contención dejaría al operador sin
 *     poder corregir una categoría mal cargada.
 *   · **No normaliza nada.** No recorta espacios de lo que se guarda, no
 *     deduplica temas y no arregla mayúsculas. Normalizar acá y no en el módulo
 *     produce dos verdades sobre el mismo texto.
 */

import type {
  NuevaCategoriaDeGuardrail,
  TextosDelChatbot,
  TextosOpcionalesDelChatbot,
} from "@contracts/admin-api/index.ts"
import { TOPE_AL_PARTICIPANTE } from "./borde-configuracion.ts"
import { SLUG_DE_LA_ENCUESTA, type MensajeConDireccion } from "./rutas.ts"

/**
 * Cuántos caracteres tiene un texto, **contando emojis como uno**.
 *
 * `§5.6`: *«Aceptan emojis, saltos de párrafo y formato de párrafo.»* `String.length`
 * cuenta unidades UTF-16, así que un emoji cuenta dos y algunos cuentan siete: el
 * contador diría que un mensaje se pasó cuando no se pasó. Se itera por puntos de
 * código, que es lo más cerca que llega el navegador sin una librería.
 */
export function largo(texto: string): number {
  return [...texto].length
}

/**
 * **Los NUEVE textos que un participante LEE.** `§5.6`.
 *
 * Siete del motor (`§4 Sección 3`) y dos de derivación (`§4 Sección 4`).
 *
 * ⛔ `personalityProfile` **no está acá y no puede estarlo**: ver el encabezado.
 *
 * ⭐⭐ **RASTRO · `R2-UI9` · 23/9/2026 — ERAN OCHO.** `ADR-0082`: el renglón decía
 * «Los OCHO textos… Seis del motor y dos de derivación», y lo único que cambió es
 * el conteo del motor. `contracts/admin-api/index.ts` ⟦la-tabla-gana-al-entorno⟧
 * lo dice con las mismas palabras: *«desde R2 son DIEZ campos en 3 secciones, de
 * los cuales NUEVE llegan al participante […] `botonSinRespuesta` entra en la
 * Sección 3 y SÍ se le manda al participante. El único que nunca sale como mensaje
 * sigue siendo `personalityProfile`.»*
 *
 * ⛔ **`botonSinRespuesta` va ACÁ y no sólo en `TODOS_LOS_TEXTOS`**, y la
 * diferencia es lo que `CA-67` mide: estar en esta lista es lo que le pone el tope
 * de 4.096 (el «N / 4096» que `PRD_R2.md:1196–1201` pide en la columna Largo) **y**
 * lo que lo hace obligatorio por herencia. Ponerlo sólo en `TODOS_LOS_TEXTOS`
 * dejaría un mensaje que el participante lee sin tope, que es el modo de falla que
 * el encabezado de este archivo describe para el perfil **al revés**.
 */
export const CAMPOS_AL_PARTICIPANTE: readonly (keyof TextosDelChatbot)[] = [
  "offTopicMessage",
  "mensajeSinDatoDuro",
  "mensajeSinCoberturaEnElBanco",
  "fallbackText",
  "avisoDeDemora",
  "pedidoDeMensajeDeTexto",
  "botonSinRespuesta",
  "mensajeDeNoDerivacion",
  "mensajeQueRemiteAlBoton",
]

/**
 * **Los DIEZ obligatorios.** `§5.4`: el perfil también, y sin default.
 *
 * ⭐ `R2-UI9` · eran NUEVE hasta el 23/9/2026; `RT-11` (`PRD_R2.md:1267`) los pone
 * en diez: *«El guardado de los mensajes del motor exige los siete: sin «Botón sin
 * respuesta» responde 4xx.»*
 */
export const TODOS_LOS_TEXTOS: readonly (keyof TextosDelChatbot)[] = [
  "personalityProfile",
  ...CAMPOS_AL_PARTICIPANTE,
]

/** Los campos obligatorios que están vacíos. `§5.4`, y el caso `R1-F2-04`. */
export function textosVacios(
  textos: TextosDelChatbot,
): readonly (keyof TextosDelChatbot)[] {
  return TODOS_LOS_TEXTOS.filter((campo) => textos[campo].trim() === "")
}

/** Los campos que se pasan del tope de la plataforma. `§5.6`, y el caso `R1-F2-07`. */
export function textosPasadosDeTope(
  textos: TextosDelChatbot,
): readonly (keyof TextosDelChatbot)[] {
  return CAMPOS_AL_PARTICIPANTE.filter((campo) => largo(textos[campo]) > TOPE_AL_PARTICIPANTE)
}

/**
 * Si los diez textos se pueden guardar.
 *
 * ⭐ **El perfil de más de 4.096 caracteres NO lo impide** (`R1-F2-09`, que es un
 * caso POSITIVO): no está en `CAMPOS_AL_PARTICIPANTE`.
 */
export function puedeGuardarLosTextos(textos: TextosDelChatbot): boolean {
  return textosVacios(textos).length === 0 && textosPasadosDeTope(textos).length === 0
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ `R3-UI2` · LA OCTAVA FILA ES OPCIONAL, Y LA OBLIGATORIEDAD PASA A SER
 *     **POR FILA**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⛔⛔ **B15 · la encuesta NO entra en `CAMPOS_AL_PARTICIPANTE` ni en
 * `TODOS_LOS_TEXTOS`, y no es una cuestión de orden.** Estar en esas listas es lo
 * que le pone a un campo el tope **y**, por la MISMA herencia que el docblock de
 * `CAMPOS_AL_PARTICIPANTE` escribe para `botonSinRespuesta`, la obligatoriedad:
 * metida ahí «para heredar el tope», «Guardar mensaje» quedaría deshabilitado con
 * la encuesta vacía y `R3-F4-CA-11` —que pide guardarla vacía— saldría rojo.
 * Además **no compilaría**: `encuestaDeSatisfaccion` no es `keyof TextosDelChatbot`,
 * es `keyof TextosOpcionalesDelChatbot` (`R3-CAA1`). El tope se le aplica acá
 * abajo, por su cuenta.
 *
 * ⛔ **Y `puedeGuardarLosTextos` deja de ser la condición del botón del panel**
 * (`inventario:163`). Era global: el panel de un mensaje se apagaba por el vacío
 * de OTRO. Con la encuesta eso sería fatal —una instancia nueva tiene los siete
 * vacíos en la base y la encuesta no se podría guardar nunca—, pero el defecto ya
 * existía para los siete. `puedeGuardarLosTextos` **se conserva** porque sigue
 * siendo la regla de lo que el cable acepta, y de ella cuelga
 * `puedeGuardarLaConfiguracion`; lo que cambia es quién la usa.
 *
 * ⚠️ **El `PUT` sigue llevando TODO** (B16): con alguno de los siete vacío en la
 * base, guardar la encuesta recibe un `400` del servidor. La pantalla lo muestra
 * como cualquier otro `4xx` del guardado y **no lo evita inventando textos**.
 */

/**
 * **Si el texto de la encuesta se pasa del tope.** `R3-F4-CA-07` · RT 7 (`:1840`).
 *
 * ⭐ El tope es el mismo `4.096` de los nueve que el participante lee —la encuesta
 * también le llega—, y llega acá **anclado al contrato**: `TOPE_AL_PARTICIPANTE`
 * está declarado en `borde-configuracion.ts` como
 * `TOPE_AL_PARTICIPANTE: typeof TOPE_DE_TEXTO_AL_PARTICIPANTE = 4096`, así que un
 * número tipeado mal de este lado no existe — no hay ningún número de este lado.
 *
 * ⛔ **Esto es lo ÚNICO que la pantalla le revisa al texto de la encuesta.**
 * `PRD_R3.md:1772`: *«Fuera del largo máximo, el texto de la encuesta no tiene
 * ninguna otra revisión: ni la pantalla ni el servidor revisan que tenga un link
 * ni cómo empieza.»*
 */
export function encuestaPasadaDeTope(opcionales: TextosOpcionalesDelChatbot): boolean {
  return largo(opcionales.encuestaDeSatisfaccion) > TOPE_AL_PARTICIPANTE
}

/**
 * ⭐⭐ **La regla de UNA fila**: si «Guardar mensaje» se habilita con el texto que
 * hay en el panel de ese mensaje. `PRD_R3.md:1770`, textual: *«Deshabilitado
 * mientras el texto pase los 4.096 caracteres y, **en los siete primeros
 * mensajes**, mientras esté vacío. En «Encuesta de satisfacción» un texto vacío se
 * puede guardar.»*
 *
 * ⛔ **El vacío se decide con `trim()`** y no con `=== ""`: `PRD_R3.md:1772` dice
 * que un texto de la encuesta con solo espacios se guarda vacío, y `CA-06` pide
 * que en los siete «el texto borrado entero» apague el botón — un texto de tres
 * espacios está borrado entero para el participante que lo lee.
 *
 * ⚠️ **No mira los otros siete mensajes, y eso es el cambio.** Lo que el cable
 * acepta lo decide `puedeGuardarLaConfiguracion`; lo que este panel puede
 * *intentar* guardar, esta función.
 */
export function puedeGuardarEsteMensaje(slug: MensajeConDireccion, texto: string): boolean {
  if (largo(texto) > TOPE_AL_PARTICIPANTE) return false
  if (slug === SLUG_DE_LA_ENCUESTA) return true
  return texto.trim() !== ""
}

/**
 * **Si la configuración entera se puede publicar.** Es la condición del botón de
 * guardado de la PANTALLA, que guarda los diez textos **más** la encuesta en un
 * solo `PUT` (`§5.2` · B16).
 *
 * ⛔ **La encuesta suma su tope y NO suma obligatoriedad**, que es exactamente la
 * asimetría que B15 pide: `""` es un valor válido y 4.097 caracteres no lo son.
 * Sin esta función el botón de pantalla mandaría alegremente una encuesta de
 * 4.097 y se comería un `400` que la propia pantalla podía evitar.
 */
export function puedeGuardarLaConfiguracion(
  textos: TextosDelChatbot,
  opcionales: TextosOpcionalesDelChatbot,
): boolean {
  return puedeGuardarLosTextos(textos) && !encuestaPasadaDeTope(opcionales)
}

/** Un campo señalado, con el motivo que el operador tiene que leer. */
export interface ProblemaDeCampo {
  readonly campo: string
  readonly motivo: string
}

/**
 * Los problemas de una categoría de guardrail. Vacío ⇒ se puede guardar.
 *
 * Cubre `R1-F2-12` (contención sin línea de ayuda), `R1-F2-13` (sin ningún tema)
 * y `R1-F2-08` (la suma del tope en contención).
 *
 * ⭐ **Se discrimina por `tipo`, igual que el contrato**: cada rama pide lo suyo y
 * ninguna pide lo de la otra. Un chequeo plano de los seis campos aceptaría una
 * moderación con línea de ayuda, que es lo que el tipo existe para prohibir.
 */
export function problemasDeLaCategoria(
  categoria: NuevaCategoriaDeGuardrail,
): readonly ProblemaDeCampo[] {
  const problemas: ProblemaDeCampo[] = []

  if (categoria.nombre.trim() === "") {
    problemas.push({ campo: "nombre", motivo: "El nombre de la categoría es obligatorio." })
  }

  // `§4 S2` pide «al menos una» etiqueta. El tipo del contrato **no puede
  // expresar «no vacía»** para nada que venga del cable, y lo dice: «la regla
  // vive donde se puede hacer cumplir». Acá es donde se puede.
  const temasCargados = categoria.temas.filter((tema) => tema.trim() !== "")
  if (categoria.temas.length === 0 || temasCargados.length === 0) {
    problemas.push({
      campo: "temas",
      motivo: "Hace falta al menos un tema: son las expresiones que disparan esta categoría.",
    })
  }

  if (categoria.tipo === "contencion") {
    if (categoria.lineaDeAyuda.trim() === "") {
      problemas.push({
        campo: "lineaDeAyuda",
        motivo:
          "En Contención la línea de ayuda es obligatoria: es el nombre y el número del recurso oficial que recibe quien está en crisis.",
      })
    }
    // ⭐⭐ LA SUMA, Y NO CADA CAMPO POR SU CUENTA. Los dos salen como UN solo
    //    envío, así que lo que se corta es el final: la línea de ayuda.
    const enviado = largo(categoria.mensaje ?? "") + largo(categoria.lineaDeAyuda)
    if (enviado > TOPE_AL_PARTICIPANTE) {
      problemas.push({
        campo: "mensaje",
        motivo: `El mensaje y la línea de ayuda salen juntos en un solo envío y suman ${enviado} caracteres, sobre un tope de ${TOPE_AL_PARTICIPANTE}.`,
      })
    }
  }

  if (categoria.tipo === "moderacion") {
    if (categoria.mensaje.trim() === "") {
      problemas.push({
        campo: "mensaje",
        motivo: "En Moderación el mensaje es obligatorio y no hay ninguno genérico del que tirar.",
      })
    }
    if (largo(categoria.mensaje) > TOPE_AL_PARTICIPANTE) {
      problemas.push({
        campo: "mensaje",
        motivo: `El mensaje tiene ${largo(categoria.mensaje)} caracteres, sobre un tope de ${TOPE_AL_PARTICIPANTE}.`,
      })
    }
  }

  return problemas
}
